/**
 * Gaver.  OA-002, 2026-09-30.
 *
 * The mosque's Vipps gifts and where each payment stands — for the treasurer.
 * No giver is named here: a gift is a reference, an amount and a purpose, and
 * whether a member is linked. An authorised gift that is not captured is
 * shown at the top, because Vipps only reserves the money for a while.
 * «Trekk nå» (an authorised gift) asks Vipps again and captures it — a person
 * deciding, like the giver's own check in the app — so it is two clicks whose
 * second names the amount, and it needs donations.capture (QA A14, 2026-10-16:
 * it was one click and allowed with read-only donations.read). «Sjekk nå» on
 * any other open gift only asks, e.g. whether a received one was refunded.
 * The amount is what the gift counts as: refunds are taken off; amounts are
 * written as Norwegians write them («99,50 kr»). A date range narrows the list;
 * the header says how many there are, and when only the newest 500 are shown.
 */
import React, { useState } from 'react';
import { can, errText, post, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { kr, osloTime } from '../lib/format';
import { Btn, Card, Field, LoadError, Loading, Msg, Tag, TwoStep, type TagTone } from '../components/ui';

const PAY_NO: Record<string, [string, TagTone]> = {
  initiated: ['startet', 'grey'], awaiting_user: ['venter på giveren', 'grey'], create_failed: ['ikke startet eller ikke bekreftet — sjekkes hos Vipps', 'grey'],
  authorized: ['reservert — ikke trukket', 'gold'], capture_pending: ['trekkes …', 'gold'], capture_failed: ['trekk avvist', 'red'],
  captured: ['mottatt', 'green'], refunded: ['tilbakebetalt', 'grey'], aborted: ['avbrutt', 'grey'], expired: ['utløpt', 'grey'], terminated: ['avsluttet', 'grey'],
  not_created: ['ikke opprettet', 'grey'], unknown: ['ukjent tilstand', 'red'],
};
const PURPOSE_NO: Record<string, string> = { general: 'Der det trengs mest', building: 'Byggefond', zakat: 'Zakat', sadaqah: 'Sadaqah', qurbani: 'Qurbani',
  fitrana: 'Fitrana', langar: 'Langar' };   // fitrana and langar: gift purposes of their own since 2026-10-29
/** Reserved money that a click captures: «Trekk nå». Anything else still open only asks Vipps: «Sjekk nå». */
export const CAPTURABLE = ['authorized', 'capture_failed'];
const CLOSED = ['refunded', 'aborted', 'expired', 'terminated', 'not_created'];

/** The header line: how many, and whether the list is cut (it stopped at 500 without a word). */
export function countLine(d: Body): string {
  const total = Number(d.total ?? (d.items || []).length), shown = Number(d.shown ?? (d.items || []).length);
  const head = total === 1 ? '1 gave' : `${total} gaver`;
  return shown < total ? `${head} · viser de ${shown} nyeste — velg en kortere periode for å se resten` : head;
}

/**
 * «Faste givere» (fast giver, 2026-10-31): how many standing gifts run now, what they come to a month, how many charges
 * failed in the last 30 days, and how many ended after failed charges — numbers only, never a giver (as the list above).
 * Absent while the mosque does not have the capability (the server sends null).
 */
export function recurringLine(r: Body): string {
  const active = Number(r.active || 0);
  const head = active === 1 ? '1 fast giver' : `${active} faste givere`;
  return `${head} · ${kr(Number(r.monthlyNok || 0))} i måneden`;
}
function RecurringCard({ r }: { r: Body }) {
  const failed = Number(r.failedLast30d || 0), ended = Number(r.ended || 0);
  return (
    <section aria-label="Faste givere" style={{ margin: '6px 18px' }}>
      <p className="field note" role="status" style={{ margin: 0 }}>
        <strong>Faste givere:</strong> {recurringLine(r)}
        {' · '}{failed === 0 ? 'ingen mislykkede trekk siste 30 dager' : <Tag tone="red">{`${failed} mislykket${failed === 1 ? '' : 'e'} trekk siste 30 dager`}</Tag>}
        {ended > 0 ? ` · ${ended} stoppet etter tre mislykkede trekk på rad` : ''}
      </p>
      <p className="field note" style={{ margin: '4px 0 0' }}>Trukne faste gaver står i listen under som vanlige gaver. Giveren stopper eller endrer en fast gave selv, i appen eller i Vipps.</p>
    </section>
  );
}

export function Donations() {
  const [range, setRange] = useState({ from: '', to: '' });
  const q = [range.from && 'from=' + range.from, range.to && 'to=' + range.to].filter(Boolean).join('&');
  const { data, error, reload } = useLoad<Body>('/donations' + (q ? '?' + q : ''));
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState<string | null>(null);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const nc = data.notCaptured || {};
  // The server says; an older server without `canCapture` falls back to the session's permission.
  const mayCapture = data.canCapture ?? can('donations.capture');
  const check = async (ref: string) => {
    setBusy(ref);
    const r = await post(`/donations/${encodeURIComponent(ref)}/check`).finally(() => setBusy(null));
    if (!r.ok) return say(errText(r.body), 'err');
    say(r.body.captured ? `${ref}: trukket.` : `${ref}: ${(PAY_NO[r.body.payStatus] || [r.body.payStatus])[0]}.`, r.body.captured ? 'ok' : '');
    reload();
  };
  const items: Body[] = data.items || [];
  const received = data.received || null;
  return (
    <Card title="Gaver">
      {nc.count > 0 && (
        // A standing notice (not a result), so it stays on the page instead of going up as a toast.
        <p className={'msg' + (nc.failed ? ' err' : '')} role="status">{`${nc.count} ${nc.count === 1 ? 'gave er' : 'gaver er'} reservert, men ikke trukket`
          + (nc.oldestStartedAt ? ` (eldste fra ${osloTime(nc.oldestStartedAt)})` : '') + (nc.failed ? `, ${nc.failed} avvist av Vipps.` : '.')}</p>
      )}
      {data.vipps && (!data.vipps.configured || data.vipps.problem_no) && (
        // OA-066: read-only. The platform operator sets up the mosque's Vipps merchant; there are no keys here.
        // Shown only while something is wrong (not set up, or a problem): a working setup says nothing (owner, 2026-10-22).
        <p className="field note" style={{ margin: '6px 18px' }}>
          <Tag tone="red">{`Vipps-betaling: ${data.vipps.state_no || (data.vipps.configured ? 'aktiv' : 'ikke klar')}`}</Tag>
          {data.vipps.msn ? ` Brukersted (MSN) ${data.vipps.msn} · ${data.vipps.env === 'production' ? 'live' : 'testmiljø'}`
            + ` · ${data.vipps.keys === 'partner' ? 'via Masjid (partner)' : 'egne Vipps-nøkler'}${data.vipps.verified ? ' · bekreftet' : ''}.` : ' Ikke satt opp.'}
          {data.vipps.problem_no ? ` ${data.vipps.problem_no}` : ''}
          {' '}Pengene går rett til moskeens egen Vipps-konto. Endringer gjøres av Masjid.
        </p>
      )}
      {!mayCapture && <p className="field note" style={{ margin: '6px 18px' }}>Å trekke en gave krever tilgangen «Trekke reserverte gaver» (Tilganger).</p>}
      <div className="row" style={{ margin: '6px 18px', alignItems: 'flex-end' }}>
        <Field label="Fra dato" className="field half" style={{ maxWidth: 180 }}>{(id) => <input id={id} type="date" value={range.from} max={range.to || undefined} onChange={(e) => setRange({ ...range, from: e.target.value })} />}</Field>
        <Field label="Til dato" className="field half" style={{ maxWidth: 180 }}>{(id) => <input id={id} type="date" value={range.to} min={range.from || undefined} onChange={(e) => setRange({ ...range, to: e.target.value })} />}</Field>
        {(range.from || range.to) && <Btn ghost onClick={() => setRange({ from: '', to: '' })}>Vis alle</Btn>}
      </div>
      <p className="field note" role="status" style={{ margin: '6px 18px' }}>
        {countLine(data)}{received ? ` · mottatt ${kr(received.amount)} fra ${received.count === 1 ? '1 gave' : received.count + ' gaver'}` : ''}
        {range.from || range.to ? ` (${range.from || '…'} – ${range.to || '…'}, Oslo-tid)` : ''}
      </p>
      <Msg text={msg.text} tone={msg.tone} />
      {data.recurring && <RecurringCard r={data.recurring} />}
      <table className="t">
        <thead><tr><th scope="col">Referanse</th><th scope="col">Beløp</th><th scope="col">Formål</th><th scope="col">Betaling</th><th scope="col">Startet</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
        <tbody>{items.map((d: Body) => {
          const [label, tone] = PAY_NO[d.payStatus] || [d.payStatus || '—', 'grey' as TagTone];
          // A received gift can still be refunded in the Vipps portal later: «Sjekk nå» stays, and only asks.
          const open = !CLOSED.includes(d.payStatus);
          return (
            <tr key={d.reference}>
              <td><code>{d.reference}</code>{d.memberLinked ? '' : <span className="note"> · uten medlem</span>}</td>
              <td>{kr(d.amount)}{d.refundedAmount ? <span className="note"> · {kr(d.refundedAmount)} tilbakebetalt</span> : ''}</td>
              <td>{PURPOSE_NO[d.purpose] || d.purpose}</td>
              <td><Tag tone={tone}>{label}</Tag></td>
              <td>{osloTime(d.startedAt)}</td>
              <td>{open && mayCapture && (CAPTURABLE.includes(d.payStatus)
                ? <TwoStep ghost={false} label="Trekk nå" busyLabel="Trekker …" disabled={busy !== null} ariaLabel={`Trekk ${kr(d.amount)} for ${d.reference}`}
                    confirmLabel={`Klikk igjen: trekk ${kr(d.amount)} fra giverens konto`} onConfirm={() => check(d.reference)} />
                : <Btn ghost disabled={busy !== null} aria-label={`Sjekk ${d.reference} hos Vipps`} onClick={() => check(d.reference)}>{busy === d.reference ? 'Sjekker …' : 'Sjekk nå'}</Btn>)}</td>
            </tr>);
        })}</tbody>
      </table>
      {!items.length && <p className="field note" style={{ margin: '10px 18px' }}>{range.from || range.to ? 'Ingen Vipps-gaver i denne perioden. Velg en annen periode, eller «Vis alle».' : 'Ingen Vipps-gaver ennå.'}</p>}
    </Card>
  );
}
